/*
 * article-recommendations.js — Dynamic product-rec hydration for blog rail (P1-198)
 *
 * Lazy-loads (IntersectionObserver, below the fold) the Shopify Ajax Product
 * Recommendations API for retail/specialty clusters, keyed off a representative
 * product id rendered into [data-product-rec][data-rec-product-id].
 *
 *   GET /recommendations/products.json?product_id={id}&intent=related&limit={n}
 *
 * On a non-empty response it builds priced cards matching the server markup and
 * swaps the server-rendered fallback for the live grid. On empty/error it leaves
 * the server fallback in place (graceful degradation, no CLS — both grids occupy
 * the same reserved geometry). B2B/research/medical clusters never reach this file
 * (they render a static consultation card with no [data-product-rec] hook), so no
 * public pricing is ever fetched for private B2B clusters.
 */
(function () {
  'use strict';

  function track(name, detail) {
    if (window.Prarvi && typeof window.Prarvi.track === 'function') {
      window.Prarvi.track(name, detail);
    }
  }

  function formatMoney(cents, fallbackStr) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function' && window.Shopify.money_format) {
      try {
        return window.Shopify.formatMoney(cents, window.Shopify.money_format);
      } catch (e) {
        /* fall through */
      }
    }
    if (fallbackStr) return fallbackStr;
    if (typeof cents === 'number') return '$' + (cents / 100).toFixed(2);
    return '';
  }

  function buildCard(product) {
    var a = document.createElement('a');
    a.className = 'article-prod-rec-card';
    a.href = product.url;
    a.setAttribute('data-rec-track', 'product');
    a.setAttribute('data-rec-product', product.handle || '');

    var media = document.createElement('span');
    media.className = 'article-prod-rec-card__media';
    var imgSrc = product.featured_image || (product.images && product.images[0]);
    if (imgSrc) {
      var img = document.createElement('img');
      img.src = imgSrc;
      img.alt = product.title || '';
      img.width = 64;
      img.height = 64;
      img.loading = 'lazy';
      media.appendChild(img);
    }
    a.appendChild(media);

    var body = document.createElement('span');
    body.className = 'article-prod-rec-card__body';

    var name = document.createElement('span');
    name.className = 'article-prod-rec-card__name';
    name.textContent = product.title || '';
    body.appendChild(name);

    if (typeof product.price === 'number' && product.price > 0) {
      var price = document.createElement('span');
      price.className = 'article-prod-rec-card__price';
      price.textContent = formatMoney(product.price, product.price_formatted);
      body.appendChild(price);
    }
    a.appendChild(body);
    return a;
  }

  function hydrate(root) {
    if (root.hasAttribute('data-rec-loaded')) return;
    root.setAttribute('data-rec-loaded', '');

    var productId = root.getAttribute('data-rec-product-id');
    var limit = parseInt(root.getAttribute('data-rec-limit'), 10) || 3;
    var cluster = root.getAttribute('data-rec-cluster') || 'retail';
    var grid = root.querySelector('[data-rec-grid]');
    var fallback = root.querySelector('[data-rec-fallback]');
    if (!productId || !grid) return; // nothing to key off -> keep server fallback

    var url =
      '/recommendations/products.json?product_id=' + encodeURIComponent(productId) + '&intent=related&limit=' + limit;

    fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (res) {
        if (!res.ok) throw new Error('rec http ' + res.status);
        return res.json();
      })
      .then(function (data) {
        var products = (data && data.products) || [];
        if (!products.length) return; // empty -> keep server fallback in place

        var frag = document.createDocumentFragment();
        products.slice(0, limit).forEach(function (p) {
          frag.appendChild(buildCard(p));
        });
        grid.innerHTML = '';
        grid.appendChild(frag);
        grid.hidden = false;
        if (fallback) fallback.hidden = true;
        track('recommendation_view', {
          cluster: cluster,
          source: 'blog',
          count: products.length,
        });
      })
      .catch(function () {
        /* keep server fallback; no CLS, no thrown error to the user */
      });
  }

  function init() {
    var nodes = document.querySelectorAll('[data-product-rec]');
    if (!nodes.length) return;

    // Click tracking (delegated) for both live + fallback cards and consult CTAs.
    document.addEventListener('click', function (e) {
      var el = e.target.closest && e.target.closest('[data-rec-track]');
      if (!el) return;
      track('recommendation_click', {
        kind: el.getAttribute('data-rec-track'),
        cluster: el.getAttribute('data-rec-cluster') || '',
        product: el.getAttribute('data-rec-product') || '',
      });
    });

    if (!('IntersectionObserver' in window)) {
      // No IO support -> hydrate immediately rather than never.
      nodes.forEach(hydrate);
      return;
    }
    var io = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            hydrate(entry.target);
            obs.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '200px 0px' },
    );
    nodes.forEach(function (n) {
      io.observe(n);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
